import React, {useCallback, useEffect, useMemo, useState} from "react";

import { ColumnsType } from "antd/es/table";
import { Button, Input, message, Space, Table } from "antd";

import {
    clockTimeValidator,
    clockTimeValidator24Hours, formatDateToClockTime,
    parseClockTimeToDate
} from "../../../../utils/Validator";
import {PowerPackData} from "../../../../types/PowerPackData";
import {
    addPowerPackRow, deletePowerPackByPowId,
    fetchPowerPackByPid,
    updatePowerPackRow
} from "../../../../backendInteraction/PowerPackApiFetch";

interface PowerPackProps {
    data: PowerPackData[];
    plodId: number;
    contractNo: string;
    isView: boolean;
    isAddNewRow: boolean | null;
    setIsAddNewRow: React.Dispatch<React.SetStateAction<boolean>> | null;
}

const PowerPackTable: React.FC<PowerPackProps> = ( {data, plodId, contractNo, isView, isAddNewRow, setIsAddNewRow })=> {
    const [powerPackData, setPowerPackData] = useState<PowerPackData[]>([]);
    const [editingPowId, setEditingPowId] = useState<number | null>(null);
    const [editData, setEditData] = useState<PowerPackData | null>(null);

    useEffect(() => {
        setPowerPackData(data);
    }, [data]);

    useEffect(() => {
        if (isAddNewRow === true && editingPowId !== -1 ) {
            const newRow: PowerPackData = {
                key: -1,
                pow_id: -1,
                time_from: "",
                time_to: "",
                pid: plodId ?? -1,
            }
            setPowerPackData(prevData => {
                const hasNewRow = prevData.some(item => item.pow_id === -1);
                return hasNewRow ? prevData : [...prevData, newRow];
            })

            setEditingPowId(-1);
            setEditData(newRow);
        }
    }, [isAddNewRow, editingPowId]);


    useEffect(() => {
        if (isView) {
            if (editingPowId === -1) {
                setPowerPackData(prevData => prevData.filter(item => item.pow_id !== -1));
                setIsAddNewRow && setIsAddNewRow(false);
            }

            setEditingPowId(null);
            setEditData(null);
        }
    }, [isView])

    const PowerPackTimeValueValidator =  (start: string, finish: string) => {
        if (!start && !finish) return true;

        if (!start || !finish) {
            message.error('One of "Start Hour" or "Finish Hour" value is empty!');
            return false;
        }

        if ((!clockTimeValidator(start) && !clockTimeValidator24Hours(start)) || (!clockTimeValidator(finish) && !clockTimeValidator24Hours(finish))) {
            message.error('Time value should be in the format hh:mm; like 0:00 for 0am/12pm and 18:00 for 6pm');
            return false;
        }
        return true;
    };


    const saveChanges = useCallback( async () => {
        console.log("Saving changes:", editData);

        if(!editData) return;

        const { time_from, time_to } = editData;

        if (!PowerPackTimeValueValidator(time_from, time_to)) return;

        const start = parseClockTimeToDate(time_from);
        const finish = parseClockTimeToDate(time_to);
        const formattedStart = formatDateToClockTime(start);
        const formattedFinish = formatDateToClockTime(finish);

        const updatedEditData = { ...editData, time_from: formattedStart || '',
            time_to: formattedFinish || '' };
        setEditData(updatedEditData);

        const storedUser = localStorage.getItem("DORS_USER");
        const aid = storedUser && JSON.parse(storedUser).Aid;

        if (editingPowId === -1) {
            const success = await addPowerPackRow(updatedEditData, aid);
            if (success) {
                setPowerPackData( await fetchPowerPackByPid(plodId ?? -1, aid));
                message.success("New power pack added successfully!");
            } else {
                message.error("Failed to add new power pack.");
            }
        } else {
            const success = await updatePowerPackRow(editingPowId ?? -1, updatedEditData);
            if (success) {
                setPowerPackData( await fetchPowerPackByPid(plodId ?? -1, aid));
                message.success("Power pack updated successfully!");
            } else {
                message.error("Failed to update power pack.");
            }
        }

        if (editingPowId === -1 && setIsAddNewRow) {
            setIsAddNewRow(false);
        }

        setEditingPowId(null);
        setEditData(null);
    }, [editData, editingPowId,setIsAddNewRow]);

    const cancelEditing = useCallback((record: PowerPackData) => {
        console.log("Cancel editing");
        setEditingPowId(null);
        setEditData(null);

        if (record.pow_id === -1) {
            setPowerPackData(prevData => prevData.filter(item => item.pow_id !== -1));
            setIsAddNewRow && setIsAddNewRow(false);
        }
    }, [setIsAddNewRow]);

    const startEditing = useCallback((record: PowerPackData) => {
        console.log("Editing row :", record);
        if ( editingPowId === -1 ) {
            setPowerPackData(prevData => prevData.filter(item => item.pow_id !== -1));
            setIsAddNewRow && setIsAddNewRow(false);
        }

        setEditingPowId( prevId => {
            if (prevId !== null) cancelEditing({pow_id: prevId} as PowerPackData);
            return record.pow_id;
        });

        setEditData({ ...record }) ;
    }, [editingPowId, setIsAddNewRow]);

    const deleteRow = useCallback(async (record: PowerPackData) => {
        console.log("Deleting row:", record);

        const storedUser = localStorage.getItem("DORS_USER");
        const aid = storedUser && JSON.parse(storedUser).Aid;

        const success = await deletePowerPackByPowId(record.pow_id);
        if (success) {
            setPowerPackData(await fetchPowerPackByPid(plodId ?? -1, aid));
            message.success("Power pack deleted successfully!");
        } else {
            message.error("Failed to delete power pack.");
        }
    }, []);

    const handleInputChange = useCallback((key: keyof PowerPackData, value: string) => {
        if (editData) {
            setEditData(prevData => ({
                ...prevData!,
                [key]: value || "",
            }));
        }
    }, [editData]);


    const columns: ColumnsType<PowerPackData> = useMemo(() => [
        {
            title: 'Power Pack Hours From',
            dataIndex: 'metres',
            key: 'metres',
            width: "33%",
            render: (_, record) =>
                editingPowId === record.pow_id ? (
                    <Input
                        value={editData?.time_from}
                        onChange={(e) => handleInputChange("time_from", e.target.value)}
                    />
                ) : (
                    record.time_from
                ),
        },
        {
            title: 'Power Pack Hours To',
            dataIndex: 'volume',
            key: 'volume',
            width: "33%",
            render: (_, record) =>
                editingPowId === record.pow_id ? (
                    <Input
                        value={editData?.time_to}
                        onChange={(e) => handleInputChange("time_to", e.target.value)}
                    />
                ) : (
                    record.time_to
                ),
        },
        ...(!isView
                ? [{
                    title: 'Action',
                    key: 'action',
                    width: "34%",
                    render: (_: any , record: PowerPackData) => (
                        <Space size="small">
                            <Space size="small">
                                <Button color="geekblue" variant="text"
                                        onClick={() => (editingPowId === record.pow_id ? saveChanges() : startEditing(record))}>
                                    {editingPowId === record.pow_id ? "Save" : "Edit"}
                                </Button>
                                <Button color="geekblue" variant="text"
                                        onClick={() => (editingPowId === record.pow_id ? cancelEditing(record) : deleteRow(record))}>
                                    {editingPowId === record.pow_id ? "Cancel" : "Delete"}
                                </Button>
                            </Space>
                        </Space>
                    ),
                }] as ColumnsType<PowerPackData>
                : []
        ),
    ], [isView, editData, handleInputChange, saveChanges, startEditing, cancelEditing, deleteRow]);

    return (
        <Table<PowerPackData>
            columns={columns}
            pagination={false}
            dataSource={powerPackData}
        />
    );
};

export default PowerPackTable;